import { useConfirm } from 'Util/hooks/useConfirm'
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { t } from 'ttag'

interface UnsavedChangesContextValue {
  hasUnsavedChanges: boolean
  markEdited: () => void
  clearEdited: () => void
  confirmForUnsavedChanges: (onConfirm: () => void) => void
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null)

interface UnsavedChangesProviderProps {
  children: ReactNode
}

export function UnsavedChangesProvider({ children }: Readonly<UnsavedChangesProviderProps>) {
  const { confirm } = useConfirm()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)

  const markEdited = useCallback(() => {
    setHasUnsavedChanges(true)
  }, [])

  const clearEdited = useCallback(() => {
    setHasUnsavedChanges(false)
  }, [])

  // Confirmation methods
  const confirmForUnsavedChanges = useCallback(
    (onConfirm: () => void) => {
      if (hasUnsavedChanges) {
        confirm({
          title: t`Are you sure?`,
          message: t`All changes will be lost. Are you sure you want to cancel?`,
          handleConfirm: () => {
            clearEdited()
            onConfirm()
          }
        })
      } else {
        onConfirm()
      }
    },
    [hasUnsavedChanges, confirm, clearEdited]
  )

  const contextValue = useMemo(
    (): UnsavedChangesContextValue => ({
      hasUnsavedChanges,
      markEdited,
      clearEdited,
      confirmForUnsavedChanges
    }),
    [hasUnsavedChanges, markEdited, clearEdited, confirmForUnsavedChanges]
  )

  return (
    <UnsavedChangesContext.Provider value={contextValue}>{children}</UnsavedChangesContext.Provider>
  )
}

export function useUnsavedChanges(): UnsavedChangesContextValue {
  const context = useContext(UnsavedChangesContext)
  if (!context) {
    throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider')
  }

  return context
}
